import api from './api'
import type { RequisicaoFalhou } from './api'
import type {
  CodigoErroSimulacao,
  SimulacaoComissaoRequest,
  SimulacaoComissaoResponse,
} from '@/types/simulacaoComissao'

const BASE_URL = '/api/v1/simulacoes/comissao'

/**
 * Simulação unificada de comissão (Rodada 2 / DEV-5) — `POST/GET /v1/simulacoes/comissao`.
 *
 * INDICATIVA e versionada no backend (D3: simular ≠ pagar). A tela manda as entradas e
 * mostra o que voltou; nenhum número é calculado aqui. Produto, parceiro e modelo vêm
 * da projeção da reserva no módulo `simulacao` — a UI nunca os envia.
 *
 * O interceptor de `api` já devolve o corpo; 201 (snapshot gravado) e 200 (nada gravado,
 * `RESERVA_NAO_SINCRONIZADA`) são ambos sucesso. Taxas na API são FRAÇÃO (0,05 = 5%).
 */
export const simulacaoComissaoService = {
  /**
   * `chaveIdempotencia` vai no header `Idempotency-Key` (Decisões 20/10 §9.2): reenviar a MESMA
   * intenção com a mesma chave devolve o snapshot já gravado (200, `reprocessamento`), sem duplicar.
   * P4 (AN-PP4 §4.1): OBRIGATÓRIA — sem ela o backend responde 400 `IDEMPOTENCIA_OBRIGATORIA`.
   */
  simular: async (
    request: SimulacaoComissaoRequest,
    chaveIdempotencia: string
  ): Promise<SimulacaoComissaoResponse> => {
    const config = { headers: { 'Idempotency-Key': chaveIdempotencia } }
    return (await api.post<SimulacaoComissaoResponse>(BASE_URL, request, config)) as unknown as SimulacaoComissaoResponse
  },

  /** Histórico de simulações da reserva (mais recente primeiro); vazio fora do escopo. */
  listarPorReserva: async (oportunidadeId: string): Promise<SimulacaoComissaoResponse[]> => {
    const lista = await api.get<SimulacaoComissaoResponse[]>(
      `${BASE_URL}?oportunidadeId=${encodeURIComponent(oportunidadeId)}`
    )
    return (lista as unknown as SimulacaoComissaoResponse[]) ?? []
  },
}

/**
 * Texto de reserva por código de erro 400 da simulação (Onda P0) — usado só quando o backend não
 * mandou `message`; a mensagem do backend (com as datas) sempre tem prioridade.
 */
export const MENSAGENS_ERRO_SIMULACAO: Record<CodigoErroSimulacao, string> = {
  DATA_REFERENCIA_INVALIDA:
    'Data de referência inválida: ela não pode ser futura nem anterior à criação da reserva.',
  CAMPANHA_NAO_APLICAVEL:
    'A fórmula da Campanha de Incentivo não se aplica ao produto desta reserva. Simule pela política do produto ou simule a Campanha sem reserva.',
  PRODUTO_RETIRADO: 'Produto retirado do catálogo. Não há simulação disponível.',
  POLITICA_COMERCIAL_EM_DEFINICAO:
    'Simulação indisponível. Política comercial do produto em definição pela Originei.',
  OUTRA_NECESSIDADE: 'Esta necessidade depende de análise da Originei e não possui simulação automática.',
  POLITICA_PRODUTO_AUSENTE: 'Não há política vigente para o produto desta reserva — revisão administrativa.',
  RESERVA_ENCERRADA: 'A reserva está encerrada: não há comissão a estimar.',
  // ── P4 (AN-PP4 §4.1) ──
  IDEMPOTENCIA_OBRIGATORIA:
    'A simulação foi recusada por falta da chave de idempotência — nada foi calculado nem gravado. Simule de novo.',
  IDEMPOTENCIA_CHAVE_INVALIDA:
    'A chave de idempotência enviada é inválida — nada foi calculado nem gravado. Simule de novo.',
  IDEMPOTENCIA_CONFLITO:
    'Esta chave de idempotência já foi usada por outra simulação — nada foi gravado. Simule de novo.',
  VALOR_OPERACAO_OBRIGATORIO: 'Informe o valor da operação: ele só é opcional nos modelos 360.',
}

/** P4: códigos que invalidam a chave da intenção em curso — o próximo envio gera chave nova. */
export const CODIGOS_IDEMPOTENCIA: ReadonlySet<string> = new Set([
  'IDEMPOTENCIA_OBRIGATORIA',
  'IDEMPOTENCIA_CHAVE_INVALIDA',
  'IDEMPOTENCIA_CONFLITO',
])

/** `2026-10-21` → `21/10/2026` em qualquer ponto do texto (sem passar por Date: sem fuso). */
const datasLegiveis = (texto: string): string =>
  texto.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_, a: string, m: string, d: string) => `${d}/${m}/${a}`)

/**
 * Mensagem legível de uma falha da simulação: a do backend (datas ISO viram dd/mm/aaaa) ou, se
 * ausente, o texto de reserva do código. `null` quando a falha não é um dos códigos conhecidos.
 */
export const mensagemErroSimulacao = (falha: unknown): string | null => {
  const corpo = (falha as RequisicaoFalhou | undefined)?.data as
    | { errorCode?: string; message?: string }
    | undefined
  const codigo = corpo?.errorCode as CodigoErroSimulacao | undefined
  if (!codigo || !(codigo in MENSAGENS_ERRO_SIMULACAO)) return null
  const doBackend = corpo?.message?.trim()
  return doBackend ? datasLegiveis(doBackend) : MENSAGENS_ERRO_SIMULACAO[codigo]
}

// ─── P1 (AN-PP1 §3.2) — backend DEV-PP1-S em paralelo; ajustar na integração ───

/**
 * Texto de reserva do bloqueio `RESERVA_NAO_SINCRONIZADA` quando o backend não manda mensagem.
 * Na P1 ele também chega para a linha PARCIAL criada por transferência ainda sem produto
 * (antes respondia `OUTRA_NECESSIDADE` por engano) — pode vir como BLOQUEADA_PARCEIRO ou
 * BLOQUEADA_POLITICA; a tela trata pelo motivo, não pelo estado.
 */
export const MENSAGEM_RESERVA_NAO_SINCRONIZADA =
  'Os dados desta reserva ainda estão chegando à Simulação. Aguarde alguns instantes e simule de novo — nada foi gravado.'
